import { Handle, Position, type NodeProps } from "@xyflow/react";
import { Tooltip, Typography, theme } from "antd";
import { useTranslation } from "react-i18next";
import { labelFor, STATUS_KIND_HINT, STATUS_KIND_LABEL } from "../terms";
import { PILL_WIDTH } from "./layout";

const { Text } = Typography;

/** Tint per lifecycle kind, so the bands read as a progression. */
const LANE_TINT: Record<string, (t: any) => string> = {
  Draft: (t) => t.colorFillQuaternary,
  DataEntry: (t) => t.colorFillQuaternary,
  Approval: (t) => `${t.purple6}0e`,
  Active: (t) => `${t.colorSuccess}0e`,
  Closed: (t) => t.colorFillQuaternary,
  Other: (t) => t.colorFillQuaternary,
};

export type LaneNodeData = {
  statusName?: string;
  kind: string;
  width: number;
  height: number;
  /** How many steps run under this state, for the caption. */
  steps: number;
};

/**
 * A band of canvas covering the steps that run under one lifecycle state.
 *
 * This is the piece that joins the two halves of the engine on screen. The
 * stages and the statuses are one machine, and every previous version of this
 * designer showed them as two unrelated lists -- stages on a page, statuses
 * behind a drawer -- so nothing said that sending a case for approval is what
 * opens the signature slots. A band labelled with the state, wrapped around
 * exactly the steps that happen in it, says that without prose.
 *
 * The band is also the way in to editing the lifecycle: clicking it opens the
 * state it names.
 */
export const LaneNode = ({ data }: NodeProps) => {
  const d = data as unknown as LaneNodeData;
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const tint = (LANE_TINT[d.kind] ?? LANE_TINT.Other)(token);

  return (
    <div
      style={{
        width: d.width,
        height: d.height,
        borderRadius: token.borderRadiusLG,
        background: tint,
        border: `1px dashed ${token.colorBorderSecondary}`,
        pointerEvents: "none",
      }}
    >
      <Tooltip
        title={
          <>
            <div>{t(labelFor(STATUS_KIND_LABEL, d.kind))}</div>
            <div style={{ opacity: 0.75, marginTop: 4 }}>
              {t(labelFor(STATUS_KIND_HINT, d.kind))}
            </div>
          </>
        }
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            margin: "8px 0 0 12px",
            padding: "2px 10px",
            borderRadius: 999,
            background: token.colorBgContainer,
            border: `1px solid ${token.colorBorderSecondary}`,
            pointerEvents: "all",
          }}
        >
          <Text style={{ fontSize: 11, fontWeight: 600 }}>
            {d.statusName ? t(d.statusName) : t("Not set")}
          </Text>
          <Text type="secondary" style={{ fontSize: 10 }}>
            {/* i18next is configured with English source strings as keys and
                no plural suffixes, so "{{count}} steps" renders "1 steps".
                Two keys is the honest fix here. */}
            {d.steps === 1
              ? t("1 step")
              : t("{{count}} steps", { count: d.steps })}
          </Text>
        </div>
      </Tooltip>
    </div>
  );
};

export type PillNodeData = { label: string; sub: string; tone: "start" | "end" };

/** Where a case enters and leaves the process. */
export const PillNode = ({ data }: NodeProps) => {
  const d = data as unknown as PillNodeData;
  const { token } = theme.useToken();
  const colour = d.tone === "start" ? token.colorPrimary : token.colorSuccess;
  return (
    <div
      style={{
        width: PILL_WIDTH,
        padding: "6px 14px",
        borderRadius: 999,
        textAlign: "center",
        background: token.colorBgContainer,
        border: `1.5px solid ${colour}`,
        color: colour,
      }}
    >
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <div style={{ fontSize: 12, fontWeight: 600 }}>{d.label}</div>
      <div style={{ fontSize: 10, opacity: 0.75 }}>{d.sub}</div>
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
    </div>
  );
};
